import React, { useState, useEffect } from "react";
import API from "../../services/api";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { t } from "@lingui/macro";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Reports = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [labels, setLabels] = useState([]);
  const [ticketsData, setTicketsData] = useState([]);
  const [reservationsData, setReservationsData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [manualReport, setManualReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    generateMonthLabels();
    fetchMonthlyData(year);
  }, [year]);

  const generateMonthLabels = () => {
    const months = [
      t`Jan`, t`Feb`, t`Mar`, t`Apr`, t`May`, t`Jun`,
      t`Jul`, t`Aug`, t`Sep`, t`Oct`, t`Nov`, t`Dec`
    ];
    setLabels(months);
  };

  const fetchMonthlyData = async (selectedYear) => {
    try {
      const tickets = [];
      const reservations = [];
      for (let i = 1; i <= 12; i++) {
        const ticketsResponse = await API.get(`/api/reports/tickets/month`, {
          params: { month: i, year: selectedYear },
        });
        const reservationsResponse = await API.get(`/api/reports/reservations/month`, {
          params: { month: i, year: selectedYear },
        });
        tickets.push(ticketsResponse.data);
        reservations.push(reservationsResponse.data);
      }
      setTicketsData(tickets);
      setReservationsData(reservations);
      setError(null);
    } catch (err) {
      setError(t`Error fetching monthly data.`);
    }
  };

  const fetchManualReport = async () => {
    if (!startDate || !endDate) {
      setError(t`Please select a start and end date.`);
      return;
    }

    setLoading(true);
    try {
      const response = await API.get(`/api/reports/generate`, {
        params: { startDate, endDate },
      });
      setManualReport(response.data);
      setError(null);
    } catch (err) {
      setError(t`Error generating manual report.`);
    } finally {
      setLoading(false);
    }
  };

  const chartData1 = {
    labels,
    datasets: [
      {
        label: t`Tickets Sold`,
        data: ticketsData,
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      }
    ]
  };

  const chartData2 = {
    labels,
    datasets: [
      {
        label: t`Reservations Made`,
        data: reservationsData,
        backgroundColor: "rgba(255, 99, 132, 0.6)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: t`Month`,
        },
      },
      y: {
        title: {
          display: true,
          text: t`Count`,
        },
      },
    },
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">{t`Reports`}</h1>
      {error && <div className="text-red-500 mb-4">{error}</div>}

      <div className="mb-4">
        <label htmlFor="year" className="mr-4 font-semibold">
          {t`Select Year:`}
        </label>
        <select
          id="year"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="border rounded-lg p-2"
        >
          {Array.from({ length: 10 }, (_, i) => (
            <option key={i} value={new Date().getFullYear() - i}>
              {new Date().getFullYear() - i}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">{t`Tickets`}</h2>
        <div className="bg-white p-4 rounded-lg shadow-lg" style={{ height: "400px" }}>
          <Bar data={chartData1} options={chartOptions} />
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">{t`Reservations`}</h2>
        <div className="bg-white p-4 rounded-lg shadow-lg" style={{ height: "400px" }}>
          <Bar data={chartData2} options={chartOptions} />
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-semibold mb-4">{t`Generate Reports`}</h2>
        <div className="flex items-center space-x-4 mb-4">
          <input
            type="date"
            placeholder={t`Start Date`}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded-lg p-2"
          />
          <input
            type="date"
            placeholder={t`End Date`}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border rounded-lg p-2"
          />
          <button
            onClick={fetchManualReport}
            className="add-new-btn"
            disabled={loading}
          >
            {loading ? t`Generating...` : t`Generate Report`}
          </button>
        </div>

        {manualReport && (
          <div className="text-gray-700">
            <h3 className="font-semibold mb-2">{t`Manual Report`}</h3>

            <div className="mb-6">
              <h4 className="font-semibold mb-2">{t`General Results`}</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>{t`Total Revenue:`}</strong>{" "}
                  MXN {manualReport.totalTicketsRevenue + manualReport.totalReservationsRevenue}
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold mb-2">{t`Ticket Details`}</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>{t`Total Tickets Sold:`}</strong> {manualReport.ticketCount}
                </li>
                <li>
                  <strong>{t`Total Revenue from Tickets:`}</strong>{" "}
                  MXN {manualReport.totalTicketsRevenue}
                </li>
              </ul>

              <div className="mt-4">
                <h5 className="font-semibold mb-2">
                  {t`Ticket Breakdown by Payment Method`}
                </h5>
                <ul className="list-disc pl-6 space-y-2">
                  {manualReport.ticketSummaries.map((item, index) => (
                    <li key={index}>
                      <strong>{item.paymentMethod}:</strong>{" "}
                      {item.ticketCount} {t`tickets`}, {t`Total Amount:`} MXN {item.totalAmount}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold mb-2">{t`Reservation Details`}</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>{t`Total Reservations Made:`}</strong>{" "}
                  {manualReport.reservationCount}
                </li>
                <li>
                  <strong>{t`Total Revenue from Reservations:`}</strong>{" "}
                  MXN {manualReport.totalReservationsRevenue}
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
